import { Button, Card, CardHeader, Drawer, EmptyState, Icon, Table, useToast, type IconName } from "@helmr/ui";
import { localDateOf } from "@helmr/contracts";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { isApiError } from "../../api/client";
import { listAudit, listStaffNames, type AuditEntry } from "../../api/audit";
import { logout, logoutAll } from "../../api/auth";
import { addDays } from "../../lib/dates";
import { Field, fmtDateTime, Load, mapError, Page, TableSkeleton, useScope } from "./kit";
import { useServices } from "../../app/services";
import { NoAccess } from "../../app/NoAccess";
import s from "./settings.module.css";
import { section, sectionAllows, type SettingsSection } from "./sections";
import { t } from "./strings";

/* ---------- audit ---------- */

/** First instant of a business-local date: the smallest instant whose local date is not before it (found through `localDateOf`, no offset arithmetic). */
export function startOfLocalDay(date: string, timeZone: string): number {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  let lo = Date.UTC(y, m - 1, d) - 15 * 3_600_000; // no zone is more than 14h from UTC
  let hi = Date.UTC(y, m - 1, d) + 15 * 3_600_000;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (localDateOf(mid, timeZone) < date) lo = mid + 1; else hi = mid;
  }
  return lo;
}

export function AuditPage() {
  const sc = useScope();
  const [entity, setEntity] = useState("");
  const [actor, setActor] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [open, setOpen] = useState<AuditEntry | null>(null);
  const tz = sc.ctx.timeZone;
  const staff = useQuery({ queryKey: sc.key("staff-names"), queryFn: () => listStaffNames(sc.client), retry: false });
  const filters = {
    entity: entity.trim() || undefined, actorStaffId: actor || undefined,
    from: from ? startOfLocalDay(from, tz) : undefined,
    // The end of a local day is one millisecond before the next one starts.
    to: to ? startOfLocalDay(addDays(to, 1), tz) - 1 : undefined,
  };
  const q = useInfiniteQuery({
    queryKey: sc.key("audit", filters), initialPageParam: null as string | null, retry: false,
    queryFn: ({ pageParam }) => listAudit(sc.client, { ...filters, cursor: pageParam }),
    getNextPageParam: (last) => last.nextCursor,
  });
  const rows = q.data?.pages.flatMap((p) => p.entries) ?? [];
  const staffName = (id: string | null) => (id ? staff.data?.find((p) => p.id === id)?.name ?? id : "System");
  return (
    <Page title={t.sections.audit.title} description={t.sections.audit.body} allow={section("audit")}>
      <Card aria-label="Audit log">
        <CardHeader title="Changes" subtitle="Newest first." />
        <div className={s.tools}>
          <input className={s.input} aria-label="Entity" placeholder="Entity, e.g. role" value={entity} onChange={(e) => setEntity(e.target.value)} />
          {staff.data ? <select className={s.select} aria-label="Actor" value={actor} onChange={(e) => setActor(e.target.value)}><option value="">Anyone</option>{staff.data.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select> : null}
          <input className={s.input} type="date" aria-label="From" value={from} onChange={(e) => setFrom(e.target.value)} />
          <input className={s.input} type="date" aria-label="To" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <Load query={q} what="the audit log" skeleton={<TableSkeleton rows={8} />}>
          {() => rows.length === 0
            ? <EmptyState compact icon="receipt" title="Nothing logged for these filters" description="Changes made in Helmr appear here." />
            : <Table label="Audit log" rows={rows} rowKey={(r) => r.id} minWidth={620} onRowClick={setOpen} hasMore={q.hasNextPage} onLoadMore={() => void q.fetchNextPage()} loadingMore={q.isFetchingNextPage} summary={`${rows.length} shown`}
              columns={[
                { key: "w", header: "When", cell: (r) => fmtDateTime(r.at, tz, sc.ctx.locale) },
                { key: "a", header: "Who", cell: (r) => staffName(r.actorStaffId) },
                { key: "x", header: "Action", cell: (r) => <button className={s.crumb} style={{ textAlign: "left" }} onClick={(e) => { e.stopPropagation(); setOpen(r); }}>{r.action}</button> },
                { key: "e", header: "Entity", cell: (r) => r.entity ?? "—" },
              ]} />}
        </Load>
      </Card>
      <Drawer open={open !== null} onOpenChange={(o) => { if (!o) setOpen(null); }} title={open?.action ?? ""} description={open ? `${staffName(open.actorStaffId)} · ${fmtDateTime(open.at, tz, sc.ctx.locale)}` : undefined}>
        {open ? (
          <div className={s.stack}>
            <dl className={s.dl}><dt>Entity</dt><dd>{open.entity ?? "—"}{open.entityId ? ` · ${open.entityId}` : ""}</dd>{open.requestId ? <><dt>Request</dt><dd>{open.requestId}</dd></> : null}</dl>
            <div className={s.diff}>
              <div><h3>Before</h3><pre className={s.audit} aria-label="Before">{open.before === null ? "—" : JSON.stringify(open.before, null, 2)}</pre></div>
              <div><h3>After</h3><pre className={s.audit} aria-label="After">{open.after === null ? "—" : JSON.stringify(open.after, null, 2)}</pre></div>
            </div>
          </div>
        ) : null}
      </Drawer>
    </Page>
  );
}

/* ---------- profile ---------- */

export function ProfilePage() {
  const sc = useScope();
  const { client, session, queryClient } = useServices();
  const nav = useNavigate();
  const toast = useToast();
  const me = sc.ctx.me;
  const [name, setName] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [restricted, setRestricted] = useState(false);
  // PATCH /staff/:id checks staff.edit_profile on the server, own profile included (the brief's "self needs no lever" is a server change).
  const canRename = sc.ctx.has("staff.edit_profile");
  const rename = useMutation({
    mutationFn: () => client.patch<{ staff: { name: string } }>(`/staff/${me!.staff.id}`, { name: (name ?? "").trim() }),
    onSuccess: async () => { await sc.qc.invalidateQueries({ queryKey: ["me"] }); toast.show(t.saved); setName(null); setErr(null); },
    onError: (e) => setErr(mapError(e, ["name"]).form),
  });
  async function out(everywhere: boolean) {
    try { await (everywhere ? logoutAll(client) : logout(client)); }
    catch (e) {
      if (everywhere && isApiError(e) && e.isForbidden) { setRestricted(true); toast.show("Signing out everywhere is not allowed for this role."); return; }
      if (everywhere && !(isApiError(e) && e.status === 401)) { toast.show("Could not sign out everywhere. Try again."); return; }
    }
    session.signOut(null);
    queryClient.clear();
    nav("/signin", { replace: true });
  }
  return (
    <Page title={t.sections.profile.title} description={t.sections.profile.body} allow={() => true}>
      <Card aria-label="Your profile">
        <CardHeader title="You" />
        {sc.ctx.status === "loading" ? <TableSkeleton rows={3} /> : !me ? <EmptyState compact icon="user" title={t.notConnected} description="Your profile is not available on this server yet." /> : (
          <div className={s.fields}>
            <Field label="Name" htmlFor="p-name" hint={canRename ? undefined : "Ask the owner to change your name."}>
              <input id="p-name" className={s.input} value={name ?? me.staff.name} disabled={!canRename} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label="Role"><span>{me.staff.roleName}</span></Field>
            <Field label="Gym"><span>{me.business.name}</span></Field>
            <Field label="Branch"><span>{me.branch?.name ?? "—"}</span></Field>
            {err ? <div className={`${s.formErr} ${s.full}`} role="alert" style={{ margin: 0 }}>{err}</div> : null}
            <div className={`${s.acts} ${s.full}`}>
              {canRename ? <Button variant="primary" busy={rename.isPending} disabled={name === null || name.trim() === "" || name.trim() === me.staff.name} onClick={() => rename.mutate()}>Save name</Button> : null}
              <Button onClick={() => void out(false)}>Sign out</Button>
              {restricted ? null : <Button onClick={() => void out(true)}>Sign out everywhere</Button>}
            </div>
          </div>
        )}
      </Card>
    </Page>
  );
}

/* ---------- index ---------- */

interface Entry { to: SettingsSection | "profile"; icon: IconName; title: string; body: string }
const GROUPS: Array<{ title: string; items: Entry[] }> = [
  { title: t.groups.gym, items: [
    { to: "business", icon: "gear", ...t.sections.business },
    { to: "branches", icon: "badge", ...t.sections.branches },
    { to: "holidays", icon: "clock", ...t.sections.holidays },
  ] },
  { title: t.groups.sell, items: [
    { to: "catalogue", icon: "receipt", ...t.sections.catalogue },
    { to: "coupons", icon: "bill", ...t.sections.coupons },
    { to: "invoicing", icon: "bill", ...t.sections.invoicing },
    { to: "memberships", icon: "users", ...t.sections.memberships },
    { to: "payment-accounts", icon: "wallet", ...t.sections.paymentAccounts },
  ] },
  { title: t.groups.people, items: [
    { to: "roles", icon: "badge", ...t.sections.roles },
    { to: "commission", icon: "cash", ...t.sections.commission },
    { to: "payroll", icon: "cash", ...t.sections.payroll },
    { to: "attendance", icon: "finger", ...t.sections.attendance },
    { to: "pt-schedule", icon: "clock", ...t.sections.ptSchedule },
  ] },
  { title: t.groups.leads, items: [
    { to: "leads", icon: "inbox", ...t.sections.leads },
    { to: "lead-stages", icon: "inbox", ...t.sections.leadStages },
    { to: "intake-keys", icon: "inbox", ...t.sections.intakeKeys },
    { to: "messaging", icon: "chat", ...t.sections.messaging },
  ] },
  { title: t.groups.devices, items: [
    { to: "devices", icon: "finger", ...t.sections.devices },
    { to: "audit", icon: "receipt", ...t.sections.audit },
  ] },
  { title: t.groups.you, items: [{ to: "profile", icon: "user", ...t.sections.profile }] },
];

/** The index of sections. Each tile is hidden for a role that cannot open it; the server enforces regardless. */
export function SettingsIndex() {
  const { ctx } = useScope();
  if (ctx.status === "loading") return <Page title={t.title} description={t.intro} back={false} allow={() => true}><TableSkeleton rows={4} /></Page>;
  const groups = GROUPS.map((g) => ({ ...g, items: g.items.filter((i) => i.to === "profile" || sectionAllows(i.to, ctx.has)) })).filter((g) => g.items.length > 0);
  // Profile is always allowed, so "nothing at all" cannot happen; a role with only that sees just the "You" group.
  if (groups.length === 0) return <NoAccess />;
  return (
    <Page title={t.title} description={t.intro} back={false} allow={() => true}>
      {groups.map((g) => (
        <section key={g.title} aria-label={g.title} className={s.page} style={{ gap: 10 }}>
          <h2 className={s.group}>{g.title}</h2>
          <div className={s.grid}>
            {g.items.map((i) => (
              <Link key={i.to} to={`/settings/${i.to}`} className={s.tileLink}>
                <span className={s.tileIcon}><Icon name={i.icon} /></span>
                <span><b>{i.title}</b><small>{i.body}</small></span>
              </Link>
            ))}
          </div>
        </section>
      ))}
    </Page>
  );
}
